import { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChatCircle, PaperPlaneRight, X } from '@phosphor-icons/react';
import { public_api } from '@/lib/api';

const WELCOME = "Hi — I'm the Inclusive Mobility Group AI assistant. Ask me anything about NDIS transport, bookings, service areas or funding. How can I help?";

export default function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([{ role: 'assistant', content: WELCOME }]);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const scrollRef = useRef(null);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
  }, [messages, open]);

  const send = async (e) => {
    e?.preventDefault();
    const text = input.trim();
    if (!text || busy) return;
    setMessages(m => [...m, { role: 'user', content: text }]);
    setInput('');
    setBusy(true);
    try {
      const res = await public_api.chat(text, sessionId);
      setSessionId(res.session_id);
      setMessages(m => [...m, { role: 'assistant', content: res.reply }]);
    } catch {
      setMessages(m => [...m, { role: 'assistant', content: "Sorry — I couldn't respond. Please try again or use our contact form." }]);
    } finally { setBusy(false); }
  };

  return (
    <>
      <button
        onClick={() => setOpen(v => !v)}
        data-testid="chat-widget-toggle"
        className="fixed bottom-6 right-4 md:right-8 z-50 w-14 h-14 md:w-16 md:h-16 rounded-full bg-brand text-white shadow-[0_8px_30px_rgba(14,125,194,0.45)] hover:shadow-[0_12px_40px_rgba(14,125,194,0.55)] flex items-center justify-center hover:-translate-y-1 transition-all group"
        aria-label="Open chat"
      >
        {open ? <X size={22} weight="bold" /> : <ChatCircle size={26} weight="fill" />}
        <span className="absolute inset-0 rounded-full animate-ping bg-brand opacity-25" />
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.96 }}
            transition={{ duration: 0.25 }}
            className="fixed bottom-24 right-4 md:right-6 z-40 w-[calc(100vw-2rem)] md:w-[400px] h-[560px] max-h-[80vh] bg-white rounded-3xl shadow-[0_25px_80px_rgba(17,24,39,0.25)] border border-neutral-200 flex flex-col overflow-hidden"
            data-testid="chat-widget-panel"
          >
            <div className="p-5 bg-ink text-white">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-lg bg-brand flex items-center justify-center">
                  <span className="text-white font-bold text-sm">S</span>
                </div>
                <div>
                  <div className="font-semibold text-sm">Inclusive Mobility Group AI Assistant</div>
                  <div className="text-xs text-white/60 flex items-center gap-1.5">
                    <span className="w-1.5 h-1.5 rounded-full bg-green-400" />
                    Online · usually replies instantly
                  </div>
                </div>
              </div>
            </div>

            <div ref={scrollRef} className="flex-1 p-4 space-y-3 overflow-y-auto bg-neutral-50">
              {messages.map((m, i) => (
                <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                  <div className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${
                    m.role === 'user'
                      ? 'bg-brand text-white rounded-br-md'
                      : 'bg-white text-ink border border-neutral-200 rounded-bl-md'
                  }`}>
                    {m.content}
                  </div>
                </div>
              ))}
              {busy && (
                <div className="flex justify-start">
                  <div className="bg-white border border-neutral-200 rounded-2xl rounded-bl-md px-4 py-3 flex gap-1">
                    <span className="w-1.5 h-1.5 rounded-full bg-ink-muted animate-bounce" style={{ animationDelay: '0ms' }} />
                    <span className="w-1.5 h-1.5 rounded-full bg-ink-muted animate-bounce" style={{ animationDelay: '150ms' }} />
                    <span className="w-1.5 h-1.5 rounded-full bg-ink-muted animate-bounce" style={{ animationDelay: '300ms' }} />
                  </div>
                </div>
              )}
            </div>

            <form onSubmit={send} className="p-3 border-t border-neutral-200 bg-white flex items-center gap-2">
              <input
                value={input}
                onChange={e => setInput(e.target.value)}
                data-testid="chat-widget-input"
                placeholder="Type your question…"
                className="flex-1 bg-neutral-100 rounded-full px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand/30"
                disabled={busy}
              />
              <button
                type="submit"
                disabled={busy || !input.trim()}
                data-testid="chat-widget-send"
                className="w-10 h-10 rounded-full bg-brand text-white flex items-center justify-center disabled:opacity-40 hover:bg-brand-hover transition"
                aria-label="Send"
              >
                <PaperPlaneRight size={16} weight="fill" />
              </button>
            </form>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
